import React, { useState, useEffect } from 'react'

import {
    Box,
    FormGroup,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
} from "@mui/material"

import LoadingButton from '@mui/lab/LoadingButton'
import SaveIcon from '@mui/icons-material/Save'

import Loading from '../../Loading'
import useNotification from '../../Notification'

import { getSession, saveSession } from "../../../auth"

import api from '../../../api'

import './index.css'

const Notifications = ({ enterpriseId }) => {
    const [loading, setLoading] = useState(false)
    const [sendNotificationOnChat, setSendNotificationOnChat] = useState(false)
    const [newMessageNotification, setNewMessageNotification] = useState(true)
    const [newMessageSound, setNewMessageSound] = useState(true)
    const [newMessageVisibility, setNewMessageVisibility] = useState('always')
    const [newChatNotification, setNewChatNotification] = useState(true)
    const [newChatSound, setNewChatSound] = useState(true)
    const [newChatVisibility, setNewChatVisibility] = useState('always')
    const [newQueueNotification, setNewQueueNotification] = useState(true)
    const [newQueueSound, setNewQueueSound] = useState(true)
    const [newQueueVisibility, setNewQueueVisibility] = useState('always')
    const [newQueueMessageNotification, setNewQueueMessageNotification] = useState(false)
    const [newQueueMessageSound, setNewQueueMessageSound] = useState(false)
    const [newQueueMessageVisibility, setNewQueueMessageVisibility] = useState('always')
    
    const defaultNotificationSettings = {
        new_message: {
            notification: true,
            sound: true,
            visibility: 'always'
        },
        new_chat: {
            notification: true,
            sound: true,
            visibility: 'always'
        },
        new_queue: {
            notification: true,
            sound: true,
            visibility: 'always'
        },
        new_queue_message: {
            notification: false,
            sound: false,
            visibility: 'always'
        }
    }

    const getNotificationSettings = () => {
        const uiSettings = getSession()?.profile?.enterprise?.settings?.notification_settings || {}

        return {
            new_message: {
                ...defaultNotificationSettings.new_message,
                ...(uiSettings.new_message || {})
            },
            new_chat: {
                ...defaultNotificationSettings.new_chat,
                ...(uiSettings.new_chat || {})
            },
            new_queue: {
                ...defaultNotificationSettings.new_queue,
                ...(uiSettings.new_queue || {})
            },
            new_queue_message: {
                ...defaultNotificationSettings.new_queue_message,
                ...(uiSettings.new_queue_message || {})
            }
        }
    }

    const updateSettings = async () => {
        setLoading(true)
        const res = await api.patch(`/api/enterprises/${enterpriseId}/`, {
            'settings': {
                'send_notification_onchat': sendNotificationOnChat,
                'notification_settings': {
                    new_message: {
                        notification: newMessageNotification,
                        sound: newMessageSound,
                        visibility: newMessageVisibility
                    },
                    new_chat: {
                        notification: newChatNotification,
                        sound: newChatSound,
                        visibility: newChatVisibility
                    },
                    new_queue: {
                        notification: newQueueNotification,
                        sound: newQueueSound,
                        visibility: newQueueVisibility
                    },
                    new_queue_message: {
                        notification: newQueueMessageNotification,
                        sound: newQueueMessageSound,
                        visibility: newQueueMessageVisibility
                    }
                }
            }
        })
        setLoading(false)

        if (res.status !== 200) {
            useNotification(
                'Ops!',
                'Algo deu errado, tente novamente.',
                'danger'
            );
            return
        }

        const session = getSession()
        session.profile.enterprise.settings.send_notification_onchat = sendNotificationOnChat
        session.profile.enterprise.settings.notification_settings = {
            new_message: {
                notification: newMessageNotification,
                sound: newMessageSound,
                visibility: newMessageVisibility
            },
            new_chat: {
                notification: newChatNotification,
                sound: newChatSound,
                visibility: newChatVisibility
            },
            new_queue: {
                notification: newQueueNotification,
                sound: newQueueSound,
                visibility: newQueueVisibility
            },
            new_queue_message: {
                notification: newQueueMessageNotification,
                sound: newQueueMessageSound,
                visibility: newQueueMessageVisibility
            },
        }
        saveSession(session)

        useNotification(
            'Sucesso!',
            'As configurações foram salvas com sucesso.',
            'success'
        )
    }

    useEffect(() => {
        const notificationSettings = getNotificationSettings()
        setSendNotificationOnChat(getSession().profile.enterprise.settings.send_notification_onchat)
        setNewMessageNotification(notificationSettings.new_message.notification)
        setNewMessageSound(notificationSettings.new_message.sound)
        setNewMessageVisibility(notificationSettings.new_message.visibility || 'always')
        setNewChatNotification(notificationSettings.new_chat.notification)
        setNewChatSound(notificationSettings.new_chat.sound)
        setNewChatVisibility(notificationSettings.new_chat.visibility || 'always')
        setNewQueueNotification(notificationSettings.new_queue.notification)
        setNewQueueSound(notificationSettings.new_queue.sound)
        setNewQueueVisibility(notificationSettings.new_queue.visibility || 'always')
        setNewQueueMessageNotification(notificationSettings.new_queue_message.notification)
        setNewQueueMessageSound(notificationSettings.new_queue_message.sound)
        setNewQueueMessageVisibility(notificationSettings.new_queue_message.visibility || 'always')
    }, [])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Loading loading={loading} />

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">
                    Campanhas WhatsApp
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 2 }}
                >
                    Permite enviar notificações de campanhas WhatsApp para usuários que
                    estão atualmente em atendimento.
                </Typography>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={sendNotificationOnChat}
                                onChange={() =>
                                    setSendNotificationOnChat(!sendNotificationOnChat)
                                }
                            />
                        }
                        label="Habilitar"
                    />
                </FormGroup>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">
                    Novo atendimento em fila
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 2 }}
                >
                    Disparado quando um novo atendimento entra em fila para ser respondido.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newQueueNotification}
                                    onChange={() =>
                                        setNewQueueNotification(!newQueueNotification)
                                    }
                                />
                            }
                            label="Exibir notificação"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newQueueSound}
                                    onChange={() =>
                                        setNewQueueSound(!newQueueSound)
                                    }
                                />
                            }
                            label="Reproduzir som"
                        />
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="new-queue-visibility-label">
                            Tocar som quando
                        </InputLabel>

                        <Select
                            labelId="new-queue-visibility-label"
                            value={newQueueVisibility}
                            label="Tocar som quando"
                            onChange={(event) =>
                                setNewQueueVisibility(event.target.value)
                            }
                        >
                            <MenuItem value="always">
                                Sempre
                            </MenuItem>

                            <MenuItem value="other_tabs">
                                Quando estiver em outra aba
                            </MenuItem>

                            <MenuItem value="outside_kwik">
                                Quando o Kwik não estiver em foco
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">
                    Nova mensagem (fila)
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 2 }}
                >
                    Disparado quando um atendimento que está na fila recebe uma nova
                    mensagem.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newQueueMessageNotification}
                                    onChange={() =>
                                        setNewQueueMessageNotification(
                                            !newQueueMessageNotification
                                        )
                                    }
                                />
                            }
                            label="Exibir notificação"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newQueueMessageSound}
                                    onChange={() =>
                                        setNewQueueMessageSound(
                                            !newQueueMessageSound
                                        )
                                    }
                                />
                            }
                            label="Reproduzir som"
                        />
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="new-queue-message-visibility-label">
                            Tocar som quando
                        </InputLabel>

                        <Select
                            labelId="new-queue-message-visibility-label"
                            value={newQueueMessageVisibility}
                            label="Tocar som quando"
                            onChange={(event) =>
                                setNewQueueMessageVisibility(event.target.value)
                            }
                        >
                            <MenuItem value="always">
                                Sempre
                            </MenuItem>

                            <MenuItem value="other_tabs">
                                Quando estiver em outra aba
                            </MenuItem>

                            <MenuItem value="outside_kwik">
                                Quando o Kwik não estiver em foco
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">
                    Novo atendimento (DAC)
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 2 }}
                >
                    Disparado quando um novo atendimento é atribuído automaticamente ao atendente pelo distribuidor de chamadas (DAC).
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newChatNotification}
                                    onChange={() =>
                                        setNewChatNotification(!newChatNotification)
                                    }
                                />
                            }
                            label="Exibir notificação"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newChatSound}
                                    onChange={() =>
                                        setNewChatSound(!newChatSound)
                                    }
                                />
                            }
                            label="Reproduzir som"
                        />
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="new-chat-visibility-label">
                            Tocar som quando
                        </InputLabel>

                        <Select
                            labelId="new-chat-visibility-label"
                            value={newChatVisibility}
                            label="Tocar som quando"
                            onChange={(event) =>
                                setNewChatVisibility(event.target.value)
                            }
                        >
                            <MenuItem value="always">
                                Sempre
                            </MenuItem>

                            <MenuItem value="other_tabs">
                                Quando estiver em outra aba
                            </MenuItem>

                            <MenuItem value="outside_kwik">
                                Quando o Kwik não estiver em foco
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6">
                    Nova mensagem (atendimento)
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 2 }}
                >
                    Disparado quando o atentende recebe uma nova mensagem em um dos seus
                    atendimentos.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newMessageNotification}
                                    onChange={() =>
                                        setNewMessageNotification(!newMessageNotification)
                                    }
                                />
                            }
                            label="Exibir notificação"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={newMessageSound}
                                    onChange={() =>
                                        setNewMessageSound(!newMessageSound)
                                    }
                                />
                            }
                            label="Reproduzir som"
                        />
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="new-message-visibility-label">
                            Tocar som quando
                        </InputLabel>

                        <Select
                            labelId="new-message-visibility-label"
                            value={newMessageVisibility}
                            label="Tocar som quando"
                            onChange={(event) =>
                                setNewMessageVisibility(event.target.value)
                            }
                        >
                            <MenuItem value="always">
                                Sempre
                            </MenuItem>

                            <MenuItem value="other_tabs">
                                Quando estiver em outra aba
                            </MenuItem>

                            <MenuItem value="outside_kwik">
                                Quando o Kwik não estiver em foco
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            <LoadingButton
                style={{ width: 'fit-content' }}
                onClick={updateSettings}
                loading={loading}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="contained"
            >
                Salvar
            </LoadingButton>
        </Box>
    )
}

export default Notifications
